import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { fetchOAuth } from '@/lib/apps-oauth';
import { useSettings } from '@/hooks/useProducts';
import { extractThemeTokens } from '@/theme/extract-tokens';
import { useAppUpdateStore } from '@/stores/app-update-store';
import { stripSessionParam, type AppManifestNavEntry } from '@/lib/apps-mounts';

interface AppEmbedProps {
  /** Rumio-side id or client_id of the registered app. */
  appId: string;
  /** Initial path on the app to render (e.g., "/meal-plans"). Reactive: when this
   *  prop changes from outside (parent navigation, popstate), the embed sends a
   *  `rumio:navigate` to the iframe so it can update its internal route without
   *  reloading. */
  path: string;
  /** Fallback base_url if the API response is missing it. */
  baseUrl?: string;
  /** Optional: forced height (px) — otherwise we auto-resize from postMessage. */
  height?: number;
  /** When true, fills the parent's height (100% via flex) instead of auto-resizing.
   *  Used by AppMountedRoute where the iframe owns a full page region. */
  fillHeight?: boolean;
  /** Rendered-ish label for the iframe's accessible name. */
  title?: string;
  className?: string;
  /** Called whenever the iframe announces its current internal path
   *  (`rumio:location`). The mount route uses this to keep the parent URL in
   *  sync. */
  onLocationChange?: (path: string) => void;
  /** When true, persist the iframe's announced path to sessionStorage and
   *  prefer it on subsequent mounts. Use for static embeds (CMS App Block)
   *  where the parent URL doesn't encode the iframe's internal path. Mount-
   *  routed embeds should leave this off — their parent URL already handles
   *  reload survival. Contract v1.1 §3.5. */
  persistPath?: boolean;
  /** Called whenever the app announces its launcher surface state
   *  (`rumio:launcher:state`). Lets the host launcher button reflect
   *  open/closed. */
  onLauncherStateChange?: (open: boolean) => void;
  /** Called when the app contributes a runtime nav menu via `rumio:nav`
   *  (contract v1.6 §3). Items replace the app's manifest-declared nav
   *  in the host header while the app's mount is active. Passing `null`
   *  (or the app sending `items: null`) clears the runtime nav and the
   *  host falls back to the manifest. */
  onNavContribute?: (items: AppManifestNavEntry[] | null) => void;
  /** Called when the app publishes notifications via `rumio:notifications`
   *  (contract v1.10). Items are pre-formatted by the app; the host renders
   *  them in its shared NotificationBell. `null` clears entries for this app. */
  onNotificationsContribute?: (payload: { items: ParsedNotificationItem[]; unreadCount: number } | null) => void;
  /** Called when the app asks the host to hide or restore its own chrome
   *  (`rumio:chrome`, contract v1.19). Only wired where the host is willing to
   *  honour it; without a handler the message is ignored. */
  onChromeChange?: (hidden: boolean) => void;
  /** Delegate `fullscreen` to the app (contract v1.19). Off unless the host
   *  opts the mount in: see `AppMountedRoute`. */
  allowFullscreen?: boolean;
}

/** Parsed shape after defensive validation of inbound `rumio:notifications`. */
export interface ParsedNotificationItem {
  id: string;
  label: string;
  sublabel?: string;
  badge?: string;
  read?: boolean;
}

/**
 * Imperative handle exposed via ref. Gives the host a way to drive the
 * launcher protocol without prop-drilling: a parent component (e.g. the
 * mount route) holds the ref, the launcher button component calls
 * `toggleLauncher()`.
 */
export interface AppEmbedHandle {
  /** Post `rumio:launcher:toggle` to the iframe. No-op until the iframe's
   *  origin has been resolved (i.e. before the initial JWT request). */
  toggleLauncher: () => void;
  /** Post `rumio:notification-action` to the iframe — used by the host
   *  NotificationBell to forward user clicks (mark-read + navigate) and
   *  mark-all-read back to the publishing app. No-op until origin resolved. */
  postNotificationAction: (action: 'click' | 'mark-all-read', id?: string) => void;
}

const PATH_STORAGE_PREFIX = 'rumio:app-path:';

/**
 * Defensive shape-check for inbound `rumio:nav` items. Returns the parsed
 * entries, an empty array, or `null` to signal "clear". Anything malformed
 * is silently dropped — we never throw inside a postMessage handler.
 *
 * Matches the server-side `validateManifest` rules in
 * functions/src/routes/admin-api/v1/index.ts: label 1-80 chars, path begins
 * with `/`, one level of nesting permitted, max 20 entries per level.
 */
const NAV_MAX_DEPTH = 2;
const NAV_MAX_PER_LEVEL = 20;
const NOTIF_MAX_ITEMS = 50;

/** Parse a `rumio:notifications` payload. Returns `null` for explicit clear,
 *  `'invalid'` to silently drop, or a parsed object. Mirrors the defensiveness
 *  of `parseRumioNavItems` — we never throw inside a postMessage handler.
 */
function parseRumioNotifications(
  data: unknown,
): { items: ParsedNotificationItem[]; unreadCount: number } | null | 'invalid' {
  if (data === null || data === undefined) return null;
  if (typeof data !== 'object' || Array.isArray(data)) return 'invalid';
  const obj = data as Record<string, unknown>;
  if (obj.items === null || obj.items === undefined) return null;
  if (!Array.isArray(obj.items)) return 'invalid';
  if (obj.items.length > NOTIF_MAX_ITEMS) return 'invalid';
  const items: ParsedNotificationItem[] = [];
  for (const raw of obj.items) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return 'invalid';
    const e = raw as Record<string, unknown>;
    if (typeof e.id !== 'string' || e.id.length === 0 || e.id.length > 128) return 'invalid';
    if (typeof e.label !== 'string' || e.label.length === 0 || e.label.length > 240) return 'invalid';
    const item: ParsedNotificationItem = { id: e.id, label: e.label };
    if (typeof e.sublabel === 'string' && e.sublabel.length <= 240) item.sublabel = e.sublabel;
    if (typeof e.badge === 'string' && e.badge.length <= 40) item.badge = e.badge;
    if (typeof e.read === 'boolean') item.read = e.read;
    items.push(item);
  }
  const unread = typeof obj.unreadCount === 'number' && obj.unreadCount >= 0
    ? Math.floor(obj.unreadCount)
    : items.filter((i) => !i.read).length;
  return { items, unreadCount: unread };
}

function parseRumioNavItems(raw: unknown, depth = 0): AppManifestNavEntry[] | null | 'invalid' {
  if (raw === null || raw === undefined) return null;
  if (!Array.isArray(raw)) return 'invalid';
  if (raw.length > NAV_MAX_PER_LEVEL) return 'invalid';
  const out: AppManifestNavEntry[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return 'invalid';
    const e = item as Record<string, unknown>;
    if (typeof e.label !== 'string' || e.label.length === 0 || e.label.length > 80) return 'invalid';
    if (typeof e.path !== 'string' || !e.path.startsWith('/')) return 'invalid';
    const entry: AppManifestNavEntry = { label: e.label, path: e.path };
    if (typeof e.icon === 'string' && e.icon.length <= 80) entry.icon = e.icon;
    if (typeof e.required_scope === 'string') entry.required_scope = e.required_scope;
    if (typeof e.required_plan_slug === 'string') entry.required_plan_slug = e.required_plan_slug;
    if (e.badge && typeof e.badge === 'object' && !Array.isArray(e.badge)) {
      const b = e.badge as Record<string, unknown>;
      if (typeof b.text === 'string' && b.text.length > 0 && b.text.length <= 40) {
        const tone = typeof b.tone === 'string' && ['neutral', 'accent', 'warning'].includes(b.tone)
          ? (b.tone as 'neutral' | 'accent' | 'warning')
          : undefined;
        entry.badge = tone ? { text: b.text, tone } : { text: b.text };
      }
    }
    if (e.children !== undefined) {
      if (depth + 1 >= NAV_MAX_DEPTH) return 'invalid';
      const parsed = parseRumioNavItems(e.children, depth + 1);
      if (parsed === 'invalid') return 'invalid';
      if (parsed && parsed.length > 0) entry.children = parsed;
    }
    out.push(entry);
  }
  return out;
}

interface SessionResponse {
  token: string;
  expires_in: number;
  session_id: string;
}

interface AppMetaResponse {
  app: { id: string; client_id: string; name: string; base_url: string };
}

const HELP_SLUG = /^[a-z0-9-]{1,80}$/;

/** Open an article in the site's Axiss widget (contract v1.23). An article the
 *  deployed widget can't open by slug falls back to its help center. */
export function openHelpArticle(slug: string): void {
  const axiss = typeof window !== 'undefined' ? window.Axiss : undefined;
  if (!axiss || !HELP_SLUG.test(slug)) return;
  if (typeof axiss.openArticle === 'function') axiss.openArticle(slug);
  else axiss.open('helpcenter');
}

/**
 * Renders an iframe embed of a Rumio-installed app. On mount:
 *   1. Looks up the app's base_url (if not passed)
 *   2. Requests a session JWT from POST /apps/session
 *   3. Renders iframe with `?rumio_session=<jwt>`
 *
 * postMessage protocol (contract v1.1 §2.1, v1.6 §3):
 *   - App → parent: { type: 'rumio:height', height }            — auto-resize
 *   - App → parent: { type: 'rumio:session:refresh' }           — request fresh JWT
 *   - App → parent: { type: 'rumio:location', path }            — internal nav inside app
 *   - App → parent: { type: 'rumio:launcher:state', open }      — launcher surface open/closed
 *   - App → parent: { type: 'rumio:nav', items }                — runtime nav contribution (v1.6)
 *   - App → parent: { type: 'rumio:notifications', items, unreadCount } — notification publish (v1.10)
 *   - App → parent: { type: 'rumio:update-available', version } — app detected a newer deploy of itself (v1.13)
 *   - App → parent: { type: 'rumio:chrome', hidden }            — hide/restore the host's header and footer (v1.19)
 *   - App → parent: { type: 'rumio:help', article }             — open an article in the site's help widget (v1.23)
 *   - Parent → app: { type: 'rumio:session', token }            — refresh response
 *   - Parent → app: { type: 'rumio:navigate', path }            — change app's internal route
 *   - Parent → app: { type: 'rumio:design', tokens }            — live theme tokens (§3.4)
 *   - Parent → app: { type: 'rumio:launcher:toggle' }           — toggle launcher surface
 *   - Parent → app: { type: 'rumio:notification-action', action, id? } — bell click/mark-all-read (v1.10)
 *   - Parent → app: { type: 'rumio:reload' }                    — hard-reload the app (v1.13)
 *
 * Path-prop changes initiated by the parent (e.g. user pressed back, or another
 * link inside Rumio shell pointed to a sibling path under the same mount) are
 * forwarded to the iframe as `rumio:navigate`. We avoid reloading the iframe
 * for path-only changes, since reload would lose iframe state and re-fetch the
 * session JWT for nothing.
 */
const AppEmbed = forwardRef<AppEmbedHandle, AppEmbedProps>(function AppEmbed(
  { appId, path, baseUrl, height, fillHeight, title, className, onLocationChange, persistPath, onLauncherStateChange, onNavContribute, onNotificationsContribute, onChromeChange, allowFullscreen = false },
  ref,
) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [resolvedBase, setResolvedBase] = useState<string | null>(baseUrl ?? null);
  const [autoHeight, setAutoHeight] = useState<number | null>(height ?? null);
  const [error, setError] = useState<string | null>(null);
  // Keep resolvedBase in a ref too so the imperative `toggleLauncher` can
  // read the current origin without re-binding the handle on every base
  // change. Without this, the ref'd toggle would close over a stale value.
  const resolvedBaseRef = useRef<string | null>(resolvedBase);
  useEffect(() => {
    resolvedBaseRef.current = resolvedBase;
  }, [resolvedBase]);

  useImperativeHandle(
    ref,
    () => ({
      toggleLauncher: () => {
        const base = resolvedBaseRef.current;
        if (!base) return;
        const allowedOrigin = new URL(base).origin;
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'rumio:launcher:toggle' },
          allowedOrigin,
        );
      },
      postNotificationAction: (action, id) => {
        const base = resolvedBaseRef.current;
        if (!base) return;
        const allowedOrigin = new URL(base).origin;
        const payload: Record<string, unknown> = { type: 'rumio:notification-action', action };
        if (id !== undefined) payload.id = id;
        iframeRef.current?.contentWindow?.postMessage(payload, allowedOrigin);
      },
    }),
    [],
  );
  // Most recent path the iframe announced via `rumio:location`. Used to suppress
  // the parent → iframe `rumio:navigate` echo when the path prop change was itself
  // caused by an iframe-originated location update.
  const lastIframeAnnouncedPathRef = useRef<string | null>(null);
  // Path the iframe was last told to render (initial src OR last rumio:navigate).
  // Lets us distinguish redundant navigates from real ones.
  const lastSentPathRef = useRef<string | null>(null);
  // Whether we've delivered the initial `rumio:design` payload. The first send
  // is gated on the iframe announcing itself (any inbound message); follow-up
  // sends fire whenever theme_config changes.
  const designDeliveredRef = useRef(false);

  const { data: settingsData } = useSettings();
  const themeConfigKey = settingsData?.settings?.theme_config
    ? JSON.stringify(settingsData.settings.theme_config)
    : null;

  const fetchToken = useCallback(async () => {
    return fetchOAuth<SessionResponse>('/apps/session', {
      method: 'POST',
      body: JSON.stringify({ app_id: appId }),
    });
  }, [appId]);

  function readPersistedPath(): string | null {
    if (!persistPath || typeof window === 'undefined') return null;
    try {
      return sessionStorage.getItem(`${PATH_STORAGE_PREFIX}${appId}`);
    } catch {
      return null;
    }
  }

  function writePersistedPath(p: string) {
    if (!persistPath || typeof window === 'undefined') return;
    try {
      sessionStorage.setItem(`${PATH_STORAGE_PREFIX}${appId}`, p);
    } catch {
      // sessionStorage may be unavailable (private mode quotas); silently skip.
    }
  }

  // Build the initial src on mount and whenever the appId changes. We do NOT
  // rebuild src when `path` changes — internal nav goes through postMessage so
  // the iframe doesn't reload.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let base = resolvedBase;
        if (!base) {
          // Public meta endpoint (contract v1.1) — replaces the prior admin-API
          // call so anonymous visitors can render embedded apps, and so we never
          // snapshot base_url into CMS rows where it can go stale.
          const meta = await fetchOAuth<AppMetaResponse>(`/apps/${appId}`);
          base = meta.app.base_url;
          if (!cancelled) setResolvedBase(base);
        }
        const session = await fetchToken();
        if (cancelled) return;
        // For static embeds, prefer the path the iframe last announced over
        // the prop's hardcoded default. This restores deep links across hard
        // reloads (contract v1.1 §3.5).
        const persistedPath = readPersistedPath();
        const initialPath = persistedPath ?? path;
        const url = new URL(initialPath, base);
        url.searchParams.set('rumio_session', session.token);
        lastSentPathRef.current = initialPath;
        // If we restored from sessionStorage, treat the initial render as
        // already-acknowledged so the path-prop effect below doesn't bounce
        // it back to the iframe via `rumio:navigate`.
        if (persistedPath) lastIframeAnnouncedPathRef.current = persistedPath;
        setSrc(url.toString());
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load app');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, fetchToken]);

  // When the parent changes `path` after initial load (popstate, programmatic
  // navigation), forward to the iframe via postMessage instead of reloading.
  useEffect(() => {
    if (!src || !resolvedBase) return;
    if (lastSentPathRef.current === path) return;
    if (lastIframeAnnouncedPathRef.current === path) {
      // The parent path change was triggered by the iframe's own location echo;
      // don't bounce it back.
      lastSentPathRef.current = path;
      return;
    }
    const allowedOrigin = new URL(resolvedBase).origin;
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'rumio:navigate', path },
      allowedOrigin
    );
    lastSentPathRef.current = path;
  }, [path, src, resolvedBase]);

  // postMessage handshake
  useEffect(() => {
    if (!resolvedBase) return;
    const allowedOrigin = new URL(resolvedBase).origin;

    function sendDesign() {
      const target = iframeRef.current?.contentWindow;
      if (!target) return;
      try {
        target.postMessage({ type: 'rumio:design', tokens: extractThemeTokens() }, allowedOrigin);
      } catch {
        // Iframe may be cross-origin and not yet ready; the next inbound
        // message will retry via the first-handshake path below.
      }
    }

    async function onMessage(ev: MessageEvent) {
      if (ev.source !== iframeRef.current?.contentWindow) return;
      if (ev.origin !== allowedOrigin) return;
      const data = ev.data as any;
      if (!data || typeof data !== 'object' || typeof data.type !== 'string') return;

      // Any inbound message proves the iframe's listener is mounted; deliver
      // the initial design payload exactly once. Follow-up sends are driven
      // by the theme-config effect below.
      if (!designDeliveredRef.current) {
        designDeliveredRef.current = true;
        sendDesign();
      }

      if (data.type === 'rumio:height' && typeof data.height === 'number' && data.height > 0) {
        setAutoHeight(data.height);
      } else if (data.type === 'rumio:session:refresh') {
        try {
          const session = await fetchToken();
          iframeRef.current?.contentWindow?.postMessage(
            { type: 'rumio:session', token: session.token },
            allowedOrigin
          );
        } catch {
          // Swallow — the app can re-request if needed.
        }
      } else if (data.type === 'rumio:location' && typeof data.path === 'string') {
        // Never persist or mirror the session token an older app build might
        // still echo in its announced path (it would land in sessionStorage
        // and the host URL bar).
        const announced = stripSessionParam(data.path);
        lastIframeAnnouncedPathRef.current = announced;
        writePersistedPath(announced);
        onLocationChange?.(announced);
      } else if (data.type === 'rumio:launcher:state' && typeof data.open === 'boolean') {
        onLauncherStateChange?.(data.open);
      } else if (data.type === 'rumio:nav') {
        // Contract v1.6 §3. `items: null` clears the runtime nav.
        const parsed = parseRumioNavItems(data.items);
        if (parsed !== 'invalid') onNavContribute?.(parsed);
      } else if (data.type === 'rumio:notifications') {
        // Contract v1.10. Whole-payload semantics: app re-publishes on every
        // poll, so the host overwrites — no incremental updates.
        const parsed = parseRumioNotifications(data);
        if (parsed !== 'invalid') onNotificationsContribute?.(parsed);
      } else if (data.type === 'rumio:chrome' && typeof data.hidden === 'boolean') {
        // Contract v1.19. A full-screen app (a game) asks for the host's
        // header and footer to go. Whether that is honoured is the mount's
        // decision, not the app's: no handler, no effect.
        onChromeChange?.(data.hidden);
      } else if (data.type === 'rumio:help' && typeof data.article === 'string') {
        // Contract v1.23. The app's "Learn more" asks the site's own help
        // widget for an article. A site without the widget ignores it.
        openHelpArticle(data.article);
      } else if (data.type === 'rumio:update-available') {
        // Contract v1.13. The embedded app's poller detected a newer deploy of
        // itself. Record it so the global UpdateAvailableBanner can surface a
        // "new version of <app>" prompt. The reload closure posts `rumio:reload`
        // to this iframe so the app hard-refreshes itself (cache/SW eviction is
        // same-origin and can't be driven from the host). Whole-state: a repeat
        // message just overwrites the entry for this appId.
        useAppUpdateStore.getState().setUpdate({
          appId,
          name: title ?? appId,
          reload: () =>
            iframeRef.current?.contentWindow?.postMessage(
              { type: 'rumio:reload' },
              allowedOrigin,
            ),
        });
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedBase, fetchToken, onLocationChange, onLauncherStateChange, onNavContribute, onNotificationsContribute, onChromeChange, persistPath, appId]);

  // Live theme-config updates: re-send `rumio:design` whenever the resolved
  // theme config changes after the initial handshake. Skip until the iframe
  // has acknowledged once — otherwise we'd post into a frame that hasn't
  // attached its listener yet and the message would be dropped.
  useEffect(() => {
    if (!resolvedBase) return;
    if (!designDeliveredRef.current) return;
    if (themeConfigKey === null) return;
    const target = iframeRef.current?.contentWindow;
    if (!target) return;
    try {
      target.postMessage({ type: 'rumio:design', tokens: extractThemeTokens() }, new URL(resolvedBase).origin);
    } catch {
      // Same swallow as above.
    }
  }, [themeConfigKey, resolvedBase]);

  // Live light/dark toggle. The host theme toggle (useTheme.ts) only flips the
  // `.dark` class on <html> and does NOT change theme_config, so the effect above
  // never fires for it. Observe the class attribute and re-send `rumio:design`
  // (which re-reads the now dark-resolved tokens + dark_mode) whenever the dark
  // state flips. Gated on the first handshake so we never post into a frame whose
  // listener isn't attached yet.
  useEffect(() => {
    if (!resolvedBase) return;
    if (typeof MutationObserver === 'undefined') return;
    const root = document.documentElement;
    let lastDark = root.classList.contains('dark');
    const observer = new MutationObserver(() => {
      const isDark = root.classList.contains('dark');
      if (isDark === lastDark) return; // ignore unrelated class mutations
      lastDark = isDark;
      if (!designDeliveredRef.current) return;
      const target = iframeRef.current?.contentWindow;
      if (!target) return;
      try {
        target.postMessage(
          { type: 'rumio:design', tokens: extractThemeTokens() },
          new URL(resolvedBase).origin,
        );
      } catch {
        // Cross-origin not ready; the next inbound message re-runs the handshake.
      }
    });
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [resolvedBase]);

  // Drop any pending "update available" entry for this app when the embed
  // unmounts — otherwise the banner would linger after the user navigates away
  // from the app, pointing at an iframe that no longer exists.
  useEffect(() => {
    return () => useAppUpdateStore.getState().clearUpdate(appId);
  }, [appId]);

  if (error) return <div role="alert" className="text-red-600 text-sm">App failed to load: {error}</div>;
  if (!src) return <div className="text-sm text-ink-muted">Loading app…</div>;

  const style: React.CSSProperties = fillHeight
    ? { height: '100%', width: '100%' }
    : { height: autoHeight ? `${autoHeight}px` : '600px' };

  return (
    <iframe
      ref={iframeRef}
      src={src}
      title={title ?? 'Rumio App'}
      className={className ?? (fillHeight ? 'w-full h-full border-0' : 'w-full border-0')}
      style={style}
      // allow-popups-to-escape-sandbox: windows the app opens (its capture
      // popup, OAuth flows) must be genuine top-level contexts — a popup that
      // inherits this sandbox list still blocks Web Speech even though
      // getUserMedia works, so dictation-in-a-popup silently fails without it.
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
      // Delegate camera + microphone permissions to the embedded app.
      // Without this attribute the parent's Permissions-Policy never
      // reaches the iframe, so any getUserMedia / SpeechRecognition call
      // in the app is silently rejected with no user-visible prompt.
      // Apps still have to be served with their own Permissions-Policy
      // allowlist that includes the parent origin — that's enforced
      // on the app side.
      //
      // web-share + clipboard-write: an app's "Share" button calls
      // navigator.share (the phone's own share sheet) and "Copy link" calls
      // navigator.clipboard.writeText. Both default to the top-level origin
      // only, so inside a cross-origin frame they reject — share with
      // NotAllowedError, copy silently — unless delegated here. Neither is
      // powerful: the share sheet and the clipboard write both need a user
      // gesture in the frame, and neither reads anything back.
      //
      // fullscreen (contract v1.19): only for a mount the host has opted in —
      // a game that owns the screen. Still needs a user gesture in the frame.
      allow={`microphone; camera; web-share; clipboard-write${allowFullscreen ? '; fullscreen' : ''}`}
      allowFullScreen={allowFullscreen || undefined}
    />
  );
});

export default AppEmbed;
